import { useState } from 'react';
import { Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { FilterAlt as FilterAltIcon, Tune as TuneIcon } from '@mui/icons-material';
import { QueryBuilderMode } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import type { QueryForm } from '../../../../hooks/QueryBuilder/useQueryForm';
import { hasAnyRule } from '../../../../utils/queryBuilderUtils';
import SimpleQueryGroup from './SimpleQueryGroup';
import AdvancedQueryBuilder from './AdvancedQueryBuilder';
import QueryToolbar from './QueryToolbar';
import ConfirmDialog from '../../ListImport/components/ConfirmDialog';

export type { QueryBuilderMode };

const toggleSx = {
    '& .MuiToggleButton-root': { textTransform: 'none', px: 3, fontWeight: 500 },
    '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'common.white', '&:hover': { bgcolor: 'primary.dark' } },
};

interface CreateQueryTabProps {
    form: QueryForm;
}

export function CreateQueryTab({ form }: CreateQueryTabProps) {
    const { t } = useLanguage();
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const { mode, setMode, simple, advanced } = form;

    const toolbar = <QueryToolbar form={form} onReset={() => setResetModalOpen(true)} />;

    const handleResetConfirm = () => {
        setResetModalOpen(false);
        form.reset();
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, newMode: QueryBuilderMode | null) => newMode && setMode(newMode)}
                    sx={toggleSx}
                >
                    <ToggleButton value="simple">
                        <FilterAltIcon sx={{ mr: 1 }} fontSize="small" />
                        {t('pages.clients.queryBuilder.createQueryTab.simpleBuilder')}
                    </ToggleButton>
                    <ToggleButton value="advanced">
                        <TuneIcon sx={{ mr: 1 }} fontSize="small" />
                        {t('pages.clients.queryBuilder.createQueryTab.advancedBuilder')}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {toolbar}

            {mode === 'simple'
                ? <SimpleQueryGroup fields={simple.simpleFields} rules={simple.rules} onUpdateField={simple.updateField} />
                : <AdvancedQueryBuilder builder={advanced} />}

            {(mode === 'simple' || hasAnyRule(advanced.groups)) && toolbar}

            <ConfirmDialog
                open={resetModalOpen}
                title={t('pages.clients.queryBuilder.createQueryTab.resetTitle')}
                message={t('pages.clients.queryBuilder.createQueryTab.resetMessage')}
                confirmText={t('pages.clients.queryBuilder.reset')}
                confirmColor="error"
                onConfirm={handleResetConfirm}
                onCancel={() => setResetModalOpen(false)}
            />
        </Box>
    );
}

export default CreateQueryTab;
